import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { 
  ArrowRight, Users, Lightbulb, Wrench, Trophy, 
  GraduationCap, Clock, Check, BookOpen, Hammer, Globe, CalendarDays
} from "lucide-react";
import { Button } from "@/components/ui/button";

const HowItWorks: React.FC = () => {
  const steps = [
    {
      number: 1,
      title: "Hear the Story",
      icon: Lightbulb,
      description: "We introduce the nonprofits we're partnering with and share the real stories behind the challenges they face. You'll understand exactly who you're building for and why it matters.",
      details: [
        "Meet representatives from partner nonprofits",
        "Understand the real-world impact of the problems",
        "See examples of what success looks like",
        "Ask questions and clarify requirements"
      ]
    },
    {
      number: 2,
      title: "Team Introductions",
      icon: Users,
      description: "Teams form around the challenges that inspire them. We mix experience levels so everyone can contribute and learn. You can come with a team or we'll help you find one.",
      details: [
        "Self-organize around problems you care about",
        "Mix of skill levels in every team",
        "Mentors assigned to support each group",
        "Clear goals and success criteria defined"
      ]
    },
    {
      number: 3,
      title: "Build Together",
      icon: Wrench,
      description: "Four hours of focused building with live support. Experienced builders code while beginners learn at training tables. Mentors float between teams to help solve problems.",
      details: [
        "Hands-on building with real tools",
        "Training tables for beginners",
        "Lightning talks throughout the day",
        "Food and refreshments provided"
      ]
    },
    {
      number: 4,
      title: "Deliver & Celebrate",
      icon: Trophy,
      description: "Teams present their solutions to the nonprofits and the community. We celebrate creativity, usefulness, and service — not competition. Working prototypes are handed off.",
      details: [
        "Demo your solution to everyone",
        "Receive feedback from nonprofit partners",
        "Hand off working tools to organizations",
        "Celebrate the impact you've made"
      ]
    }
  ];

  const preEventTimeline = [
    {
      timing: "4+ Weeks Out",
      title: "Submit or Join a Team",
      description: "Register for the event and indicate your interests. You can submit a team or register individually to be matched."
    },
    {
      timing: "2-3 Weeks Out",
      title: "Pre-Production Meetings",
      description: "Meet with nonprofit partners to understand their challenges deeply. Ask questions, gather requirements, and start thinking about solutions."
    },
    {
      timing: "1 Week Out",
      title: "Solution Pre-Planning with Coaches",
      description: "Work with experienced coaches to outline your approach. Get feedback on your plan and prepare to hit the ground running on event day."
    }
  ];

  const daySchedule = [
    { time: "11:00 AM ET", event: "Doors Open", desc: "Arrive early for networking, training tables, and coffee" },
    { time: "11:40 AM ET", event: "Hear the Story", desc: "Meet the nonprofits and hear their challenges (20 min)" },
    { time: "11:50 AM ET", event: "Team Introductions", desc: "Form teams and finalize your project scope (10 min)" },
    { time: "12:00 PM ET", event: "Build Session Begins", desc: "Hands-on building with mentor support" },
    { time: "1:00 PM ET", event: "Lightning Talks", desc: "Quick practical tips from experienced builders" },
    { time: "3:30 PM ET", event: "Final Push", desc: "Polish your solutions for presentation" },
    { time: "3:45 PM ET", event: "Showcase & Celebration", desc: "Demo your work and celebrate together" },
    { time: "4:00 PM ET", event: "Wrap-up", desc: "Hand off solutions and networking" }
  ];

  const canonicalUrl = `https://buildwithchrist.ai/how-it-works`;

  return (
    <div className="relative">
      <Helmet>
        <title>How It Works | BuildWithChrist.ai</title>
        <meta name="description" content="Learn how our 4-hour build events work — from hearing nonprofit stories to delivering working AI prototypes." />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>

      {/* Hero */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <span className="text-sm font-medium text-primary uppercase tracking-wider mb-2 block">How It Works</span>
          <h1 className="text-4xl md:text-5xl font-bold font-display mb-6">
            From Problem to Prototype in <span className="text-primary">4 Hours</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            Our build events are designed to create real, working AI solutions for nonprofits 
            while giving everyone — regardless of skill level — a chance to learn and contribute.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild>
              <Link to="/attendees#register-form">
                Register for Next Event
                <ArrowRight size={18} className="ml-2" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/events">View All Events</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Before the Event Timeline */}
      <section className="py-16 px-4 bg-primary/5 border-y border-primary/10">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-sm font-medium text-primary uppercase tracking-wider mb-2 block">Before the Event</span>
            <h2 className="text-3xl md:text-4xl font-bold font-display mb-4 flex items-center justify-center gap-3">
              <CalendarDays className="text-primary" />
              Pre-Event Timeline
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              The best solutions start before event day. Here's how teams prepare:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {preEventTimeline.map((item, idx) => (
              <div key={idx} className="bg-card border border-border rounded-xl p-6">
                <div className="text-sm font-medium text-primary mb-2">{item.timing}</div>
                <h3 className="font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="py-16 px-4 bg-card/30">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-sm font-medium text-primary uppercase tracking-wider mb-2 block">Event Day</span>
            <h2 className="text-3xl md:text-4xl font-bold font-display">The Four Phases</h2>
          </div>
          <div className="space-y-16">
            {steps.map((step, idx) => (
              <div key={step.number} className={`grid grid-cols-1 lg:grid-cols-2 gap-8 items-center ${idx % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                <div className={`space-y-4 ${idx % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="flex items-center gap-3">
                    <span className="text-5xl font-bold text-primary/20 font-display">{step.number}</span>
                    <h2 className="text-2xl md:text-3xl font-bold font-display">{step.title}</h2>
                  </div>
                  <p className="text-muted-foreground">{step.description}</p>
                  <ul className="space-y-2">
                    {step.details.map((detail, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <Check size={16} className="text-secondary shrink-0 mt-0.5" />
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={`flex justify-center ${idx % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div className="w-32 h-32 rounded-full bg-card border border-border flex items-center justify-center">
                    <step.icon size={48} className="text-primary" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attendee Callout */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-primary/5 border border-primary/20 rounded-xl p-6 md:p-8">
            <h3 className="text-xl font-semibold mb-3 font-display">You Don't Have to Build to Attend</h3>
            <p className="text-muted-foreground">
              Not ready to join a team? No problem! Attendees can observe, learn at training tables, 
              network with professionals, and support the mission without committing to build. 
              It's a great way to see what we're about before diving in.
            </p>
          </div>
        </div>
      </section>

      {/* Livestream Callout */}
      <section className="py-12 px-4 bg-secondary/10">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="p-4 bg-secondary/20 rounded-full shrink-0">
              <Globe className="text-secondary" size={32} />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-semibold mb-2 font-display">Watch from Anywhere</h3>
              <p className="text-muted-foreground mb-4">
                Can't make it in person? We livestream across YouTube, Twitch, and other platforms 
                with MCs walking around hosting for both the live and global audience. Join from 
                anywhere in the world and be part of the experience.
              </p>
              <Button asChild>
                <Link to="/attendees#register-form">Register for Livestream</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Training Tables */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-sm font-medium text-primary uppercase tracking-wider mb-2 block">For Beginners</span>
            <h2 className="text-3xl md:text-4xl font-bold font-display mb-4 flex items-center justify-center gap-3">
              <GraduationCap className="text-primary" />
              Training Tables
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              No AI experience? No problem. Our training tables provide hands-on learning 
              with mentors who will guide you through the basics and help you contribute.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-card border border-border rounded-xl p-6">
              <h3 className="font-semibold mb-3">What You'll Learn</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <Check size={14} className="text-secondary shrink-0 mt-1" />
                  How AI tools work and when to use them
                </li>
                <li className="flex items-start gap-2">
                  <Check size={14} className="text-secondary shrink-0 mt-1" />
                  Practical prompt engineering techniques
                </li>
                <li className="flex items-start gap-2">
                  <Check size={14} className="text-secondary shrink-0 mt-1" />
                  Building simple automations with no code
                </li>
                <li className="flex items-start gap-2">
                  <Check size={14} className="text-secondary shrink-0 mt-1" />
                  How to evaluate and improve AI outputs
                </li>
              </ul>
            </div>

            <div className="bg-card border border-border rounded-xl p-6">
              <h3 className="font-semibold mb-3">What You'll Create</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <Check size={14} className="text-primary shrink-0 mt-1" />
                  Your own AI-powered workflow
                </li>
                <li className="flex items-start gap-2">
                  <Check size={14} className="text-primary shrink-0 mt-1" />
                  A portfolio piece to share
                </li>
                <li className="flex items-start gap-2">
                  <Check size={14} className="text-primary shrink-0 mt-1" />
                  Connections with mentors and peers
                </li>
                <li className="flex items-start gap-2">
                  <Check size={14} className="text-primary shrink-0 mt-1" />
                  Confidence to keep learning
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Day Schedule */}
      <section className="py-20 px-4 bg-card/30">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-sm font-medium text-primary uppercase tracking-wider mb-2 block">Day Schedule</span>
            <h2 className="text-3xl md:text-4xl font-bold font-display mb-4 flex items-center justify-center gap-3">
              <Clock className="text-primary" />
              What to Expect
            </h2>
            <p className="text-muted-foreground">All times are Eastern Time (ET)</p>
          </div>

          <div className="space-y-4">
            {daySchedule.map((item, idx) => (
              <div key={idx} className="flex gap-4 items-start">
                <div className="w-24 shrink-0 text-right">
                  <span className="text-sm font-medium text-primary">{item.time}</span>
                </div>
                <div className="w-px bg-border h-16 relative">
                  <div className="absolute top-1 -left-1.5 w-3 h-3 rounded-full bg-primary" />
                </div>
                <div className="pb-4">
                  <h4 className="font-semibold">{item.event}</h4>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Start Planning CTAs */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <span className="text-sm font-medium text-primary uppercase tracking-wider mb-2 block">Get Started</span>
          <h2 className="text-3xl md:text-4xl font-bold font-display mb-4">Ready to Start?</h2>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
            Explore our resource library for AI tools and tutorials, or try the solution sandbox 
            to start planning your project before the event.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto mb-8">
            <Link 
              to="/library" 
              className="bg-card border border-border rounded-xl p-6 hover:border-primary transition-colors text-left group"
            >
              <BookOpen className="text-primary mb-3" size={28} />
              <h3 className="font-semibold mb-2 group-hover:text-primary transition-colors">Resource Library</h3>
              <p className="text-sm text-muted-foreground">
                Browse AI tools, tutorials, and guides to prepare for the event.
              </p>
            </Link>

            <Link 
              to="/sandbox" 
              className="bg-card border border-border rounded-xl p-6 hover:border-secondary transition-colors text-left group"
            >
              <Hammer className="text-secondary mb-3" size={28} />
              <h3 className="font-semibold mb-2 group-hover:text-secondary transition-colors">Solution Sandbox</h3>
              <p className="text-sm text-muted-foreground">
                Describe a problem and get AI-generated solution workflows to explore.
              </p>
            </Link>
          </div>

          <Button size="lg" asChild>
            <Link to="/attendees#register-form">
              Register for the Next Event
              <ArrowRight size={18} className="ml-2" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default HowItWorks;
